import { action, observable } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { ApplicantResponseArgs } from '@this/domain/trip/applicant';
import Applicant, { convertApplicantResponseToArgs } from '@this/domain/trip/applicant';
import Department from '@this/domain/department/department';
import type { DepartmentArgs } from '@this/domain/department/department/department';
import type { ProjectArgs } from '@this/domain/project/project';
import Project from '@this/domain/project/project';
import type { TripApproveItemResponseArgs } from '@this/domain/trip/trip_approve_item';
import TripApproveItem, { convertTripApproveItemResponseToArgs } from '@this/domain/trip/trip_approve_item';
import { roundString } from '@this/src/util';
import TravelerNameChange, {
  convertTravelerNameChangeResponseToArgs
} from '@this/domain/trip/traveler_name_change';
import type { TravelerNameChangeResponseArgs } from '@this/domain/trip/traveler_name_change';
import { TripReport } from '@this/domain/trip_report/trip_report';
import type { TripReportArgs } from '@this/domain/trip_report/trip_report';
import type { UserResponseArgs } from '../user/user2';
import User, { convertUserResponseToArgs } from '../user/user2';
import type { TravelerInformationResponseArgs } from '../traveler_information';
import TravelerInformation, { convertTravelerInformationResponseToArgs } from '../traveler_information';
import type { InAdvanceApprovalResponseArgs } from '../in_advance_approval';
import { convertInAdvanceApprovalResponseToArgs, InAdvanceApproval } from '../in_advance_approval';
import type { ArrangementRequestResponseArgs } from '../arrangement_requiest';
import ArrangementRequest, { convertArrangementRequestResponseToArgs } from '../arrangement_requiest';
import type { OrderResponseArgs } from '../order2';
import Order, { convertOrderResponseToArgs } from '../order2';
import type { NonOrderItemResponseArgs } from '../non_order_item2';
import NonOrderItem, { convertNonOrderItemResponseToArgs } from '../non_order_item2';
import type { TripRuleErrorArgs } from './trip_rule_error';
import TripRuleError from './trip_rule_error';

export type TripResponseArgs = {
  id: number;
  user: UserResponseArgs;
  search_query_id: number;
  cache_start_time: string;
  cache_end_time: string;
  start_time: string;
  end_time: string;
  checkin_date?: string;
  checkout_date?: string;
  show_department_detail: boolean;
  show_project_detail: boolean;
  peoplenum: number;
  final_destination: string;
  purpose: string;
  internal_number: string;
  flight_approve_expired_at?: string;
  status: number;
  draft: boolean;
  title?: string;
  approval_route_version: number;
  shinkansen_deadline?: string | null;
  foreign_air?: boolean;
  projects?: ProjectArgs[];
  charging_departments?: DepartmentArgs[];
  applicant?: ApplicantResponseArgs;
  current_order?: OrderResponseArgs;
  first_order?: OrderResponseArgs;
  applied_order?: OrderResponseArgs;
  traveler_informations?: TravelerInformationResponseArgs[];
  cache_rule_errors?: TripRuleErrorArgs[];
  in_advance_approval: InAdvanceApprovalResponseArgs[]; // serverから単数形で返ってくるのでここで吸収する
  current_approval_stage: number;
  arrangement_request: ArrangementRequestResponseArgs;
  non_order_items: NonOrderItemResponseArgs[];
  received_at_in_tokyo?: string;
  freee_approval_request_id?: string;
  return_time?: string;
  trip_approve_item?: TripApproveItemResponseArgs;
  ticketing_requested_at?: string;
  invoice_id?: number;
  downloaded_count?: number;
  downloaded_at?: string;
  traveler_name_changes?: TravelerNameChangeResponseArgs[];
  exic_uri?: string;
  user_id?: number;
  ticketing_requestable?: boolean;
  seat_reservation_enable?: boolean;
  cancel_requestable?: boolean;
  report_available?: boolean;
  trip_report?: TripReportArgs;
  has_unread_reply_required_messages?: boolean;
  created_at: string;
};

export type TripArgs = {
  id: number;
  user?: User;
  showFee: boolean;
  searchQueryId: number;
  cacheStartTime: Moment;
  cacheEndTime: Moment;
  startTime: Moment;
  endTime: Moment;
  checkinDate?: Moment;
  checkoutDate?: Moment;
  showDepartmentDetail: boolean;
  showProjectDetail: boolean;
  peoplenum: number;
  finalDestination: string;
  purpose: string;
  internalNumber: string;
  flightApproveExpiredAt: Moment | null;
  status: number;
  draft: boolean;
  title?: string;
  approvalRouteVersion: number;
  shinkansenDeadline: string | null;
  foreignAir: boolean;
  projects?: Project[];
  chargingDepartments?: Department[];
  applicant?: Applicant;
  order?: Order;
  currentOrder?: Order;
  firstOrder?: Order;
  appliedOrder?: Order;
  travelerInformations: TravelerInformation[];
  ruleErrors: TripRuleError[];
  inAdvanceApprovals: InAdvanceApproval[];
  currentApprovalStage: number;
  arrangementRequest: ArrangementRequest | null;
  nonOrderItems: NonOrderItem[];
  receivedAtInTokyo?: Moment;
  freeeApprovalRequestId?: string;
  returnTime: Moment | null;
  tripApproveItem?: TripApproveItem;
  ticketingRequestedAt: Moment | null;
  invoiceId?: number;
  downloadedCount?: number;
  downloadedAt: Moment | null;
  travelerNameChanges: TravelerNameChange[];
  exicUri: string;
  userId?: number;
  ticketingRequestable?: boolean;
  seatReservationEnable?: boolean;
  cancelRequestable?: boolean;
  reportAvailable?: boolean;
  tripReport?: TripReport;
  hasUnreadReplyRequiredMessages: boolean;
  createdAt: Moment;
};

export const convertTripResponseToArgs = (response: TripResponseArgs, showFee: boolean) => {
  const args: TripArgs = {
    id: response.id,
    user: response.user ? new User(convertUserResponseToArgs(response.user)) : undefined,
    showFee,
    searchQueryId: response.search_query_id,
    cacheStartTime: moment(response.cache_start_time),
    cacheEndTime: moment(response.cache_end_time),
    startTime: moment(response.start_time),
    endTime: moment(response.end_time),
    checkinDate: response.checkin_date ? moment(response.checkin_date) : undefined,
    checkoutDate: response.checkout_date ? moment(response.checkout_date) : undefined,
    showDepartmentDetail: response.show_department_detail,
    showProjectDetail: response.show_project_detail,
    peoplenum: response.peoplenum,
    finalDestination: response.final_destination,
    purpose: response.purpose,
    internalNumber: response.internal_number,
    flightApproveExpiredAt: response.flight_approve_expired_at ? moment(response.flight_approve_expired_at) : null,
    status: response.status,
    draft: response.draft,
    title: response.title,
    approvalRouteVersion: response.approval_route_version,
    shinkansenDeadline: response.shinkansen_deadline || null,
    foreignAir: response.foreign_air || false,
    projects: response.projects ? response.projects.map(project => new Project(project)) : undefined,
    chargingDepartments: response.charging_departments
      ? response.charging_departments.map(department => new Department(department))
      : undefined,
    applicant: response.applicant ? new Applicant(convertApplicantResponseToArgs(response.applicant)) : undefined,
    order: response.current_order
      ? new Order(convertOrderResponseToArgs(response.current_order, showFee))
      : undefined,
    currentOrder: response.current_order
      ? new Order(convertOrderResponseToArgs(response.current_order, showFee))
      : undefined,
    firstOrder: response.first_order
      ? new Order(convertOrderResponseToArgs(response.first_order, showFee))
      : undefined,
    appliedOrder: response.applied_order
      ? new Order(convertOrderResponseToArgs(response.applied_order, showFee))
      : undefined,
    travelerInformations:
      response.traveler_informations?.map(
        travelerInformation =>
          new TravelerInformation(convertTravelerInformationResponseToArgs(travelerInformation))
      ) || [],
    ruleErrors: response.cache_rule_errors?.map(cacheRuleError => new TripRuleError(cacheRuleError)) || [],
    inAdvanceApprovals: response.in_advance_approval.map(
      inAdvanceApproval => new InAdvanceApproval(convertInAdvanceApprovalResponseToArgs(inAdvanceApproval))
    ),
    currentApprovalStage: response.current_approval_stage,
    arrangementRequest: response.arrangement_request
      ? new ArrangementRequest(convertArrangementRequestResponseToArgs(response.arrangement_request))
      : null,
    nonOrderItems: response.non_order_items.map(
      nonOrderItem => new NonOrderItem(convertNonOrderItemResponseToArgs(nonOrderItem))
    ),
    receivedAtInTokyo: response.received_at_in_tokyo ? moment(response.received_at_in_tokyo) : undefined,
    freeeApprovalRequestId: response.freee_approval_request_id || undefined,
    returnTime: response.return_time ? moment(response.return_time) : null,
    tripApproveItem: response.trip_approve_item
      ? new TripApproveItem(convertTripApproveItemResponseToArgs(response.trip_approve_item))
      : undefined,
    ticketingRequestedAt: response.ticketing_requested_at ? moment(response.ticketing_requested_at) : null,
    invoiceId: response.invoice_id,
    downloadedCount: response.downloaded_count,
    downloadedAt: response.downloaded_at ? moment(response.downloaded_at) : null,
    travelerNameChanges:
      response.traveler_name_changes?.map(
        tnc => new TravelerNameChange(convertTravelerNameChangeResponseToArgs(tnc))
      ) || [],
    exicUri: response.exic_uri || '',
    userId: response.user_id,
    ticketingRequestable: response.ticketing_requestable,
    seatReservationEnable: response.seat_reservation_enable,
    cancelRequestable: response.cancel_requestable,
    reportAvailable: response.report_available,
    tripReport: response.trip_report ? new TripReport(response.trip_report) : undefined,
    hasUnreadReplyRequiredMessages: response.has_unread_reply_required_messages || false,
    createdAt: moment(response.created_at)
  };
  return args;
};

export default class Trip {
  @observable id: number;
  @observable user?: User;
  @observable showFee: boolean;
  @observable searchQueryId: number;
  @observable cacheStartTime: Moment;
  @observable cacheEndTime: Moment;
  @observable startTime: Moment;
  @observable endTime: Moment;
  @observable checkinDate?: Moment;
  @observable checkoutDate?: Moment;
  @observable showDepartmentDetail: boolean;
  @observable showProjectDetail: boolean;
  @observable peoplenum: number;
  @observable finalDestination: string;
  @observable purpose: string;
  @observable internalNumber: string;
  @observable flightApproveExpiredAt: Moment | null;
  @observable status: number;
  @observable draft: boolean;
  @observable title?: string;
  @observable approvalRouteVersion: number;
  @observable shinkansenDeadline: string | null;
  @observable foreignAir: boolean;
  @observable projects?: Project[];
  @observable chargingDepartments?: Department[];
  @observable applicant?: Applicant;
  @observable order?: Order;
  @observable currentOrder?: Order;
  @observable firstOrder?: Order;
  @observable appliedOrder?: Order;
  @observable travelerInformations: TravelerInformation[];
  @observable ruleErrors: TripRuleError[];
  @observable inAdvanceApprovals: InAdvanceApproval[];
  @observable currentApprovalStage: number;
  @observable arrangementRequest: ArrangementRequest | null;
  @observable nonOrderItems: NonOrderItem[];
  @observable receivedAtInTokyo?: Moment;
  @observable freeeApprovalRequestId?: string;
  @observable returnTime: Moment | null;
  @observable tripApproveItem?: TripApproveItem;
  @observable ticketingRequestedAt: Moment | null;
  @observable invoiceId?: number;
  @observable downloadedCount?: number;
  @observable downloadedAt: Moment | null;
  @observable travelerNameChanges: TravelerNameChange[];
  @observable exicUri: string;
  @observable userId?: number;
  @observable ticketingRequestable?: boolean;
  @observable seatReservationEnable?: boolean;
  @observable cancelRequestable?: boolean;
  @observable reportAvailable?: boolean;
  @observable tripReport?: TripReport;
  @observable hasUnreadReplyRequiredMessages: boolean;
  @observable createdAt: Moment;
  private readonly textLength: number = 10;

  constructor(args: TripArgs) {
    this.id = args.id;
    this.user = args.user;
    this.showFee = args.showFee;
    this.searchQueryId = args.searchQueryId;
    this.cacheStartTime = args.cacheStartTime;
    this.cacheEndTime = args.cacheEndTime;
    this.startTime = args.startTime;
    this.endTime = args.endTime;
    this.checkinDate = args.checkinDate;
    this.checkoutDate = args.checkoutDate;
    this.showDepartmentDetail = args.showDepartmentDetail;
    this.showProjectDetail = args.showProjectDetail;
    this.peoplenum = args.peoplenum;
    this.finalDestination = args.finalDestination;
    this.purpose = args.purpose;
    this.internalNumber = args.internalNumber;
    this.flightApproveExpiredAt = args.flightApproveExpiredAt;
    this.status = args.status;
    this.draft = args.draft;
    this.title = args.title;
    this.approvalRouteVersion = args.approvalRouteVersion;
    this.shinkansenDeadline = args.shinkansenDeadline;
    this.foreignAir = args.foreignAir;
    this.chargingDepartments = args.chargingDepartments;
    this.applicant = args.applicant;
    this.order = args.order;
    this.currentOrder = args.currentOrder;
    this.firstOrder = args.firstOrder;
    this.appliedOrder = args.appliedOrder;
    this.travelerInformations = args.travelerInformations;
    this.ruleErrors = args.ruleErrors;
    this.inAdvanceApprovals = args.inAdvanceApprovals;
    this.currentApprovalStage = args.currentApprovalStage;
    this.arrangementRequest = args.arrangementRequest;
    this.nonOrderItems = args.nonOrderItems;
    this.receivedAtInTokyo = args.receivedAtInTokyo;
    this.freeeApprovalRequestId = args.freeeApprovalRequestId;
    this.returnTime = args.returnTime;
    this.tripApproveItem = args.tripApproveItem;
    this.ticketingRequestedAt = args.ticketingRequestedAt;
    this.invoiceId = args.invoiceId;
    this.downloadedCount = args.downloadedCount;
    this.downloadedAt = args.downloadedAt;
    this.travelerNameChanges = args.travelerNameChanges || [];
    this.exicUri = args.exicUri;
    this.userId = args.userId;
    this.ticketingRequestable = args.ticketingRequestable;
    this.seatReservationEnable = args.seatReservationEnable;
    this.cancelRequestable = args.cancelRequestable;
    this.reportAvailable = args.reportAvailable;
    this.tripReport = args.tripReport;
    this.hasUnreadReplyRequiredMessages = args.hasUnreadReplyRequiredMessages;
    this.createdAt = args.createdAt;
  }

  get dateRange() {
    return `${this.cacheStartTime.format('YYYY/MM/DD')}〜${this.cacheEndTime.format('YYYY/MM/DD')}`;
  }

  get currentApproval() {
    return this.inAdvanceApprovals.find(
      inAdvanceApproval => inAdvanceApproval.approveStage === this.currentApprovalStage
    );
  }

  get currentApprovalStatus() {
    return this.currentApproval?.status;
  }

  get nextApproval() {
    return this.inAdvanceApprovals.find(
      inAdvanceApproval => inAdvanceApproval.approveStage === this.currentApprovalStage + 1
    );
  }

  get totalPrice() {
    return (this.order?.totalPrice || 0) + this.nonOrderItemsTotalPrice;
  }

  get totalBufferAmount() {
    return this.order?.totalBufferAmount || 0;
  }

  get totalPriceWithBuffer() {
    return (this.order?.totalPriceWithBuffer || 0) + this.nonOrderItemsTotalPrice;
  }

  get nonOrderItemsTotalPrice() {
    return this.nonOrderItems.reduce((acc, nonOrderItem) => acc + nonOrderItem.totalPrice, 0);
  }

  get oldTotalPrice() {
    return (this.firstOrder?.totalPrice || 0) + this.nonOrderItemsTotalPrice;
  }

  get didTotalPriceChange() {
    return this.totalPrice !== this.oldTotalPrice;
  }

  get isDepartmentMappingsExist() {
    return this.order?.orderItems.some(item =>
      item.orderItemMappings.some(mapping => mapping.isChargingDepartmentExist)
    );
  }

  get isDepartmentNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item =>
      item.nonOrderItemMappings.some(mapping => mapping.isChargingDepartmentExist)
    );
  }

  get isProjectMappingsExist() {
    return this.order?.orderItems.some(item => item.orderItemMappings.some(mapping => mapping.isProjectExist));
  }

  get isProjectNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item => item.nonOrderItemMappings.some(mapping => mapping.isProjectExist));
  }

  get isExpensesAccountTypeMappingsExist() {
    return this.order?.orderItems.some(item =>
      item.orderItemMappings.some(mapping => mapping.isExpensesAccountTypeExist)
    );
  }

  get isExpensesAccountTypeNonOrderItemMappingsExist() {
    return this.nonOrderItems.some(item =>
      item.nonOrderItemMappings.some(mapping => mapping.isExpensesAccountTypeExist)
    );
  }

  get isHotelCheckinExpired() {
    return this.order?.orderItems.some(item =>
      item.hotels.some(
        hotel => hotel.tooLate || (hotel.checkinDate && hotel.checkinDate.isBefore(moment(), 'day'))
      )
    );
  }

  get flightTooLateError() {
    return this.flightApproveExpiredAt && moment().isSameOrAfter(this.flightApproveExpiredAt);
  }

  get isApproved() {
    return this.inAdvanceApprovals.every(approver => approver.status === 1);
  }

  get isRejected() {
    return this.inAdvanceApprovals.some(approver => approver.status === 2);
  }

  get isApproveCanceled() {
    return this.inAdvanceApprovals.some(approver => approver.status === 3);
  }

  get isAllOrderItemStatusComplete() {
    const nonCompleteList = this.order?.orderItems.filter(item => item.status !== 13 && item.status !== 7);
    return nonCompleteList?.length === 0;
  }

  get isTicketingRequested() {
    return this.foreignAir && this.ticketingRequestedAt;
  }

  get cancelReason() {
    const cancelApproval = this.inAdvanceApprovals.find(approver => approver.status === 3);
    return cancelApproval?.cancelReason;
  }

  get nextStageApprovers() {
    const nextApprovals = this.inAdvanceApprovals.find(
      inAdvanceApproval => inAdvanceApproval.approveStage === this.currentApprovalStage + 1
    );
    return (
      nextApprovals?.inAdvanceApproverCandidates.map(
        inAdvanceApproverCandidate => inAdvanceApproverCandidate.approverCandidate.name
      ) ?? []
    );
  }

  get travelerNames() {
    return this.travelerInformations.map(traveler => traveler.travelerName);
  }

  get destinationText() {
    return roundString(this.destination, 30);
  }

  get destinationTitle() {
    const destination = this.destination;
    if (destination.length > this.textLength) {
      return destination;
    }
    return undefined;
  }

  get originText() {
    return roundString(this.departureLocation, 30);
  }

  get originTitle() {
    const departureLocation = this.departureLocation;
    return departureLocation.length > this.textLength ? departureLocation : undefined;
  }

  get cancelFeeText() {
    return this.isTicketingRequested ? (this.order?.isTicketingExpired ? '取消料 + 5,000円' : '5,000円') : '0円';
  }

  get isEndTimeInFuture() {
    const time = this.cacheEndTime || this.endTime;
    return !!time && time.isAfter(moment());
  }

  isBackStageApprover(userId: number) {
    const approvers = this.inAdvanceApprovals.filter(approver => approver.approverId === userId);
    return approvers.length > 0 && approvers.some(approver => approver.approveStage < this.currentApprovalStage);
  }

  approveReason(num: number) {
    return this.inAdvanceApprovals[num].approveReason;
  }

  rejectReason(num: number) {
    return this.inAdvanceApprovals[num].rejectReason;
  }

  get destination() {
    const from = this.departureLocation;

    // orderItemsから目的地の情報を取得
    if (this.order?.orderItems?.length) {
      const elements = this.order.orderItems.flatMap(item =>
        item.transports.filter(e => e.to !== undefined && e.to.name !== from)
      );
      return elements.length > 0 ? elements.map(e => e.to.name).join('、') : '';
    }

    // nonOrderItemsから目的地の情報を取得
    if (this.nonOrderItems.length > 0) {
      const elements = this.nonOrderItems.flatMap(item =>
        item.transports.filter(e => e.to !== undefined && e.to.name !== from)
      );
      return elements.length > 0 ? elements.map(e => e.to?.name || e.arrivalStation).join('、') : '';
    }

    return '';
  }

  get departureLocation() {
    const hasOrderItems = this.order?.orderItems && this.order.orderItems.length > 0;
    const hasNonOrderItems = this.nonOrderItems && this.nonOrderItems.length > 0;

    // orderItemsの処理
    if (hasOrderItems && this.order?.orderItems) {
      const elements = this.order.orderItems.flatMap(item => item.transports.filter(e => e.from !== undefined));
      return elements.length > 0 ? elements[0].from.name : '';
    }

    // nonOrderItemsの処理
    if (hasNonOrderItems) {
      const elements = this.nonOrderItems.flatMap(item => item.transports.filter(e => e.from !== undefined));
      return elements[0]?.from?.name || elements[0]?.boardingStation || '';
    }

    return '';
  }

  startDate(format: string) {
    const d = this.order?.startDate || this.startTime;
    return d ? d.format(format) : this.checkinDate?.format(format);
  }

  endDate(format: string) {
    const d = this.order?.endDate || this.endTime;
    return d ? d.format(format) : this.checkoutDate?.format(format);
  }

  @action
  setInternalNumber(internalNumber: string) {
    this.internalNumber = internalNumber;
  }
}
